/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ----- LANDING PAGE ANIMATIONS

class LandingPageApp {
  constructor() {
    this.decadeSections = [
      ...document.querySelectorAll(".section--decades > .decade-wrapper"),
    ];
    this.nav = document.querySelector("store-header.header");

    this._init();
  }

  _init() {
    if (this.decadeSections.length) {
      this._createLenis();
      this._renderLenis();
      gsap.registerPlugin(ScrollTrigger, Flip);
      this._createHero();
      this._animateFiveDecades();
      this._resetYearLinkBehavior();

      if (this.decadeSections.length) {
        this.decadeSections.forEach((section, i) => {
          this._createDecade(section, i);
        });
      }
      console.log("Animations have been initialized");
    }
  }

  _createLenis() {
    this.lenis = new Lenis({
      lerp: 0.05,
      easing: (t) => 1 - Math.pow(1 - t, 5),
    });
  }

  _renderLenis(time) {
    this.lenis.raf(time);
    requestAnimationFrame(this._renderLenis.bind(this));
  }

  _getElements(section) {
    if (section === "hero") {
      return {
        mainWrap: document.querySelector(".editorial_hero__video"),
        flipWrap: document.getElementById("hero_video_flip_container"),
        contentWrap: document.querySelector(".editorial_hero__content"),
        content: document.querySelector(".hero_content"),
        fiveDecadeContent: document.querySelector(
          ".editorial_hero__decade_wrapper"
        ),
        years: document.querySelector(".hero_years"),
        video: document.getElementById("hero_video_flip_element"),
      };
    } else {
      return {
        horScroll: section.querySelector(".hor-scroll"),
        horScrollWrap: section.querySelector(".hor-scroll-wrapper"),
        title: section.querySelector(".title-wrapper"),
        content: section.querySelector(".content-wrapper"),
      };
    }
  }

  _setInitState(section) {
    const { horScroll, title, content } = this._getElements(section);

    gsap.set(horScroll, {
      x: window.innerWidth,
    });

    gsap.set(title, {
      yPercent: -150,
      autoAlpha: 0,
    });

    gsap.set(content, {
      autoAlpha: 0,
      xPercent: 20,
      clipPath: "00 ",
    });

    gsap.set(section, {
      opacity: 1,
    });
  }

  _resetYearLinkBehavior() {
    const { years: yearsWrap } = this._getElements("hero");
    const years = [...yearsWrap.querySelectorAll(".year")];

    years.forEach((link) => {
      link.addEventListener("click", function (e) {
        const targetId = this.getAttribute("href");
        const target = document.getElementById(targetId);

        if (target) {
          e.preventDefault();
          history.pushState(null, null, targetId);
          target.scrollIntoView({ behavior: "auto" });
        }
      });
    });
  }

  _animateFiveDecades() {
    const navHeight = this.nav.offsetHeight;
    const heroDecadeSection = document.querySelector(
      ".editorial_hero__decades"
    );
    const downArrow = document.querySelector(".scroll_down .icon-custom.arrow");
    const decadeNav = this.nav.querySelector(".decade__nav.mobile");

    const { fiveDecadeContent } = this._getElements("hero");
    const fiveDecadeElements = fiveDecadeContent.children;

    gsap.set(fiveDecadeElements, {
      autoAlpha: 0,
      xPercent: -10,
    });

    gsap.to(fiveDecadeElements, {
      xPercent: 0,
      autoAlpha: 1,
      stagger: {
        each: 0.05,
        from: "end",
      },
      scrollTrigger: {
        trigger: heroDecadeSection,
        start: `top top+=${navHeight}`,
        toggleActions: "play none none reverse",
        onEnter: () => {
          decadeNav.classList.remove("inactive");
        },
        onLeaveBack: () => {
          decadeNav.classList.add("inactive");
        },
      },
    });

    gsap.to(downArrow, {
      fill: "var(--color-neutral)",
      scrollTrigger: {
        trigger: heroDecadeSection,
        start: "top bottom",
        end: "bottom 40%",
        toggleActions: "play reset play reset",
      },
    });
  }

  _createHero() {
    const elements = this._getElements("hero");
    const navHeight = this.nav.offsetHeight;

    const missingElements = Object.entries(elements).filter(([key, el]) => !el);
    if (missingElements.length) {
      console.log(
        "Missing elements:",
        missingElements.map(([key]) => key)
      );
      return;
    }
    if (Object.values(elements).some((el) => !el)) return;

    const { mainWrap, flipWrap, contentWrap, content, video } = elements;

    let flipCtx;
    flipCtx && flipCtx.revert();

    flipCtx = gsap.context(() => {
      const contentElements = content.children;

      gsap.set(contentElements, {
        autoAlpha: 0,
        xPercent: -10,
      });

      const endState = Flip.getState(flipWrap);
      const tweenMeetPoint = `clamp(top+=${navHeight} top+=${navHeight})`;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: mainWrap,
          start: "clamp(top 50%)",
          endTrigger: contentWrap,
          end: tweenMeetPoint,
          invalidateOnRefresh: true,
          scrub: true,
        },
      });

      // Flip animation
      tl.add(
        Flip.fit(video, endState, {
          ease: "none",
          duration: 1,
          onComplete: () => {
            const staticVideo = document.getElementById(
              "hero_video_static_element"
            );
            const decadeNav = this.nav.querySelector(".decade__nav.mobile");

            video.classList.add("hidden");
            staticVideo.classList.remove("hidden");

            gsap.to([contentElements], {
              xPercent: 0,
              autoAlpha: 1,
              stagger: {
                each: 0.05,
                from: "end",
              },
              scrollTrigger: {
                trigger: contentWrap,
                start: tweenMeetPoint,
                invalidateOnRefresh: true,
                toggleActions: "play none none reverse",
                onLeaveBack: () => {
                  video.classList.remove("hidden");
                  staticVideo.classList.add("hidden");
                  decadeNav.classList.add("inactive");
                },
              },
            });
          },
        })
      );
    });
  }

  _createDecade(section, i) {
    this._setInitState(section);
    this._animateDecadeContent(section, i);
  }

  _animateDecadeContent(section, i) {
    const { horScroll, title, content } = this._getElements(section);
    const navHeight = this.nav.offsetHeight;
    const delay = 500;

    setTimeout(() => {
      this._startAnimation(horScroll, title, content, navHeight, section, i);
    }, delay);
  }

  _startAnimation(horScroll, title, content, navHeight, section, i) {
    const scrollWidth = -(horScroll.offsetWidth - window.innerWidth - 100);
    const duration = -(scrollWidth / 100);
    const isLastSection = this.decadeSections.length - 1 === i;
    const scrollDownArrow = document.querySelector(
      ".scroll_arrow__wrapper.scroll_down"
    );

    const mainTl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: `top top+=${navHeight}`,
        end: `top+=${section.offsetHeight + 2000}px`,
        scrub: 0.5,
        pin: true,
        onEnterBack: () => {
          if (isLastSection) {
            scrollDownArrow.classList.remove("hidden");
          }
        },
        onLeave: () => {
          if (isLastSection) {
            scrollDownArrow.classList.add("hidden");
          }
        },
      },
      defaults: {
        duration: 2,
        ease: "power4.out",
      },
    });

    mainTl
      .to(title, {
        autoAlpha: 1,
      })
      .to(title, {
        yPercent: 0,
      })
      .to(
        content,
        {
          xPercent: 0,
          autoAlpha: 1,
        },
        "<50%"
      )
      .to(
        horScroll,
        {
          x: scrollWidth,
          ease: "none",
          duration: duration,
        },
        "<0%"
      );
  }
}

// ----- CUSTOM PRODUCT IMAGES

class ProductImageGallery {
  constructor() {
    this.productGalleryStr = "product_detail__images_wrapper";
    this.imageGalleryWrapper = document.getElementById(this.productGalleryStr);
    if (this.imageGalleryWrapper) {
      this.elements = this._getElements();
      this._init();
    }
  }

  _init() {
    if (!this.imageGalleryWrapper) {
      console.error(`Cannot find ${this.productGalleryStr}`);
      return;
    }
    this._initSwiper();
    this._initLightGallery();
    this._observeWrapper();
  }

  _getElements() {
    return {
      scrollBar: this.imageGalleryWrapper.querySelector(
        ".product_detail__images_scrollbar"
      ),
      pagination: this.imageGalleryWrapper.querySelector(
        ".product_detail__images_pagination"
      ),
      prev: this.imageGalleryWrapper.querySelector(
        ".product_detail__images_control.prev"
      ),
      next: this.imageGalleryWrapper.querySelector(
        ".product_detail__images_control.next"
      ),
      lightboxLink: [
        ...this.imageGalleryWrapper.querySelectorAll(
          ".product_detail__image-link"
        ),
      ],
    };
  }

  _initSwiper() {
    const imageGalleryElements = {};

    const mySwiper = new Swiper(this.imageGalleryWrapper, {
      grabCursor: true,
      loop: true,
      slidesPerView: "auto",
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      scrollbar: {
        el: this.elements.scrollBar,
        draggable: true,
      },
      pagination: {
        el: this.elements.pagination,
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 3,
      },
      navigation: {
        nextEl: this.elements.next,
        prevEl: this.elements.prev,
      },
    });
  }

  _initLightGallery() {
    const lg = lightGallery(this.imageGalleryWrapper, {
      licenseKey: "B6CDB2EB-430F-4A16-B1B6-72A05EF0EAF5",
      plugins: [lgZoom, lgThumbnail],
      addClass: "custom-product-lightbox",
      mode: "lg-fade",
      speed: 500,
      counter: false,
      download: false,
      allowMediaOverlap: true,
      thumbnail: true,
      alignThumbnails: "right",
      thumbMargin: 0,
      thumbHeight: "120px",
      showZoomInOutIcons: false,
      actualSize: false,
      selector: this.elements.lightboxLink,
      hideScrollbar: true,
    });
  }

  _observeWrapper() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.parentNode.classList.toggle(
            "-scrolled",
            !entry.isIntersecting
          );
        });
      },
      {
        rootMargin: "-100% 0px 0px 0px",
        threshold: 0,
      }
    );

    observer.observe(this.imageGalleryWrapper);
  }
}

// ----- SCROLL TO TOP BUTTON
class ScrollToTopHandler {
  constructor() {
    this.scrollButton = document.getElementById("scroll-up");
    this.scrollVal = 300;

    if (this.scrollButton) {
      this._init();
    }
  }

  _init() {
    window.onscroll = () => this._changeDisplay();
    this._scrollUp();
  }

  _scrollUp() {
    this.scrollButton.addEventListener("click", (e) => {
      window.scrollTo({
        top: 0,
        left: 0,
      });
    });
  }

  _changeDisplay() {
    this.scrollButton.style.display =
      document.body.scrollTop > this.scrollVal ||
      document.documentElement.scrollTop > this.scrollVal
        ? "block"
        : "none";
  }
}

// ----- OPEN EXTERNAL LINKS IN NEW TAB
const externalLinkHandler = {
  links: [...document.querySelectorAll("a:not(.cookie-policy)")],
  init() {
    this.links.forEach((link) => {
      if (link.host !== window.location.hostname) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noreferrer noopener");
      }
    });
  },
};

// ----- FIND NAV BAR HEIGHT
const navHeightHandler = {
  elements: {
    nav: document.querySelector("store-header.header"),
    announcement_bar: document.querySelector(
      "announcement-bar.announcement-bar"
    ),
    pre_footer: document.querySelector("section.pre-footer"),
  },
  body: document.querySelector("body"),
  setVariableStr() {
    let variableStr = "";
    for (const [key, value] of Object.entries(this.elements)) {
      const height = value?.offsetHeight || 0;
      const handleizedKey = key.split("_").join("-");
      variableStr += `--${handleizedKey}-height: ${height}px; `;
    }

    return variableStr;
  },
  init() {
    this.body.style.cssText = this.body.style.cssText + this.setVariableStr();
  },
};

class AccordionHandler {
  constructor() {
    const accordionSections = {
      faq: [...document.querySelectorAll(".faq__item")],
      filter: [...document.querySelectorAll(".product-facet__filter-item")],
      mobileNav: [
        ...document.querySelectorAll('.mobile-nav__item[data-level="1"]'),
      ],
      mobileNavChildren: [
        ...document.querySelectorAll('.mobile-nav__item[data-level="2"]'),
      ],
    };

    for (const [key, value] of Object.entries(accordionSections)) {
      if (value.length) this._init(value);
    }
  }

  _init(arr) {
    arr.forEach((item) => {
      const toggleButton = item.querySelector("button");
      toggleButton?.addEventListener("click", (e) => {
        this._closeOtherAccordions(arr, item);
      });
    });
  }

  _closeOtherAccordions(parentArr, clickedItem) {
    parentArr.forEach((item) => {
      if (item !== clickedItem) {
        const toggleButton = item.querySelector("button");
        const toggleContent = item.querySelector("collapsible-content");

        toggleButton?.setAttribute("aria-expanded", "false");
        toggleContent?.removeAttribute("open");
      }
    });
  }
}

class StickFooterHandler {
  constructor() {
    this.elements = this._getElements();

    if (!this.elements.mm.matches) return;
    this._init();
  }

  _init() {
    this._hideFooterOnScrollUp();
    this._setStickyFooterClass();
  }

  _getElements() {
    return {
      body: document.querySelector("body"),
      drawerCont: document.getElementById("facet-filters"),
      openTriggers: [
        document.querySelector(".product-facet__meta-bar-item--filter"),
        document.querySelector(".account--addresses button.account__address"),
      ],
      closeTriggers: [
        document.querySelector("facet-filters .drawer__header"),
        document.querySelector("facet-filters .drawer__overlay"),
        document.querySelector(
          "drawer-content .drawer__header .drawer__close-button"
        ),
        document.querySelector("drawer-content .drawer__overlay"),
      ],
      drawerClass: "drawer-out",
      footer: document.querySelector("footer.footer"),
      scrollVal: 20,
      mm: matchMedia("(min-width: 741px)"),
    };
  }

  _hideFooterOnScrollUp() {
    const { scrollVal, footer } = this.elements;

    const hiddenClass = "hidden";
    const hasScrolled = window.scrollY > scrollVal;

    if (!hasScrolled) footer.classList.add(hiddenClass);
    window.onscroll = () => {
      const hasScrolled = window.scrollY > scrollVal;
      footer.classList.toggle(hiddenClass, !hasScrolled);
    };
  }

  _setStickyFooterClass() {
    const { openTriggers, closeTriggers, body } = this.elements;
    const stickyClass = "sticky--footer";

    openTriggers.forEach((trigger) => {
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        body.classList.remove(stickyClass);
      });
    });

    closeTriggers.forEach((trigger) => {
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        setTimeout(() => {
          body.classList.add(stickyClass);
        }, this._getTransitionsInMs(trigger.parentNode));
      });
    });
  }

  _getTransitionsInMs(el) {
    const styles = getComputedStyle(el);
    const transitionTime = styles.transitionDuration;
    return parseFloat(transitionTime) * 1000;
  }
}

const featCollectionButtonHandler = {
  featTabs: [
    ...document.querySelectorAll(
      ".shopify-section--featured-collections .tabs-nav__item"
    ),
  ],
  init() {
    if (!this.featTabs.length) return;
    this.featTabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const text = tab.getAttribute("data-button-text");
        const url = tab.getAttribute("data-button-url");
        const activeButton = document.getElementById("active-tab-button");
        console.log(text);
        console.log(url);
        console.log(activeButton);
        if (text) {
          activeButton.textContent = text;
          activeButton.setAttribute("href", url);
          activeButton.style.display = "inline-block";
        } else {
          activeButton.style.display = "none";
        }
      });
    });
  },
};

function initScripts() {
  // ----- FIND NAV BAR HEIGHT
  navHeightHandler.init();

  // ----- OPEN EXTERNAL LINKS IN NEW TAB
  externalLinkHandler.init();

  // ----- SCROLL TO TOP BUTTON
  new ScrollToTopHandler();

  // ----- LANDING PAGE ANIMATIONS
  new LandingPageApp();

  // ----- PRODUCT IMAGES
  new ProductImageGallery();

  // ----- ACCORDION ITEMS
  new AccordionHandler();

  // ----- STICKY FOOTER HANDLER
  new StickFooterHandler();

  // ----- FEAT COLLECTION BUTTON RENDERER
  featCollectionButtonHandler.init();
}

document.addEventListener("DOMContentLoaded", () => {
  initScripts();
});
